/**
 * GridCrash — Player Titles & Badge Showcase
 *
 * Titles are earned through achievements (defs with a `title` field)
 * and equipped one at a time; the equipped title rides along with
 * global leaderboard submissions (validated server-side against
 * TITLE_ALLOWLIST — keep the Netlify function's copy in sync).
 *
 * The showcase is up to 3 earned achievements the player pins first
 * in the sidebar badge row and the Badges tab.
 *
 * Storage: localStorage with in-memory fallback, mirrored
 * fire-and-forget to profiles.equipped_title / profiles.showcase.
 */

import { ACHIEVEMENTS, getAchievementState, type Achievement } from "./achievements.js";
import { getClient, ensureSession } from "./auth.js";

/** Every title that exists — mirror this list in netlify/functions/leaderboard.js. */
export const TITLE_ALLOWLIST: string[] = ACHIEVEMENTS
  .filter((a): a is Achievement & { title: string } => typeof a.title === "string")
  .map((a) => a.title);

const SHOWCASE_MAX = 3;
const KEY = "gridcrash.titles.v1";

interface TitlesState {
  equippedTitle: string; // "" = none
  showcase: string[];    // achievement ids, display order
}

let memState: TitlesState | null = null;

function readState(): TitlesState {
  if (memState) return memState;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<TitlesState>;
      memState = {
        equippedTitle: typeof parsed.equippedTitle === "string" ? parsed.equippedTitle : "",
        showcase: Array.isArray(parsed.showcase)
          ? parsed.showcase.filter((s): s is string => typeof s === "string").slice(0, SHOWCASE_MAX)
          : [],
      };
      return memState;
    }
  } catch { /* corrupt — fall through */ }
  memState = { equippedTitle: "", showcase: [] };
  return memState;
}

function writeState(state: TitlesState): void {
  memState = state;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* memory fallback holds */ }
  mirrorToProfile(state);
}

/** Fire-and-forget server mirror, same trust model as profile.equip(). */
function mirrorToProfile(state: TitlesState): void {
  void (async () => {
    try {
      const userId = await ensureSession();
      const client = await getClient();
      if (!userId || !client) return;
      await client
        .from("profiles")
        .update({ equipped_title: state.equippedTitle, showcase: state.showcase })
        .eq("id", userId);
    } catch { /* offline — local copy stands */ }
  })();
}

// ─── Titles ───────────────────────────────────────────────────────────────────

/** Titled achievements the player has earned. */
export function getAvailableTitles(): Array<{ achievement: Achievement; title: string }> {
  const earned = new Set(getAchievementState().earned);
  return ACHIEVEMENTS
    .filter((a): a is Achievement & { title: string } =>
      typeof a.title === "string" && earned.has(a.id))
    .map((a) => ({ achievement: a, title: a.title }));
}

/** The equipped title, or "" when none. */
export function getEquippedTitle(): string {
  const state = readState();
  // An un-earned title (cleared achievements, tampered storage) never renders.
  return getAvailableTitles().some((t) => t.title === state.equippedTitle)
    ? state.equippedTitle
    : "";
}

/** Equips an earned title ("" to unequip). False when not earned. */
export function setEquippedTitle(title: string): boolean {
  if (title !== "" && !getAvailableTitles().some((t) => t.title === title)) return false;
  const state = readState();
  state.equippedTitle = title;
  writeState(state);
  return true;
}

// ─── Showcase ─────────────────────────────────────────────────────────────────

/** Pinned achievement ids in display order (all earned, max 3). */
export function getShowcase(): string[] {
  const earned = new Set(getAchievementState().earned);
  return readState().showcase.filter((id) => earned.has(id));
}

/**
 * Toggles an earned achievement in/out of the showcase.
 * Returns the new membership, or null when rejected (not earned / full).
 */
export function toggleShowcase(achievementId: string): boolean | null {
  const earned = new Set(getAchievementState().earned);
  if (!earned.has(achievementId)) return null;

  const state = readState();
  const idx = state.showcase.indexOf(achievementId);
  if (idx >= 0) {
    state.showcase.splice(idx, 1);
    writeState(state);
    return false;
  }
  if (state.showcase.length >= SHOWCASE_MAX) return null;
  state.showcase.push(achievementId);
  writeState(state);
  return true;
}
